import { isPublicRoute } from "@src/config/routes"
import { appSessionMachine } from "@src/features/auth/machines/appSessionMachine"
import { authMachine } from "@src/features/auth/machines/authMachine"
import { appRouterMachine } from "@src/machines/appRouterMachine"
import type { SessionResult } from "@src/types/session"
import { logger } from "@src/utils/logger"
import { and, assign, not, or, sendTo, setup } from "xstate"

export interface BootstrapContext {
  pathname: string
  searchRedirect: string | null
  address: string | null
  chainType: string | null
  sessionResult: SessionResult | null
  routeIntent: string | null
  /** After SIGN_OUT, ignore connects until a disconnect is observed. */
  awaitingDisconnect: boolean
}

export type BootstrapEvent =
  | {
      type: "WALLET_STATE"
      address: string | null
      chainType: string | null
      restoreStatus: "restoring" | "settled"
    }
  | {
      type: "WALLET_RESTORED"
      address: string | null
      chainType: string | null
    }
  | { type: "AUTH_INVALIDATED" }
  | { type: "VERIFICATION_COMPLETE" }
  | { type: "BANNED_DETECTED" }
  | { type: "SIGN_OUT" }
  | { type: "RETRY_SESSION" }
  | {
      type: "PATHNAME_CHANGED"
      pathname: string
      searchRedirect?: string | null
    }

/**
 * Top-level app readiness machine.
 *
 * Owns route policy and sessionResult; delegates auth/router/session to child
 * machines. Splash is limited to `initializing`-tagged states — `signed_in`
 * revalidation must stay splash-free and must not re-fire `refreshRouter`.
 */
export const appBootstrapMachine = setup({
  types: {} as {
    context: BootstrapContext
    events: BootstrapEvent
    input: { pathname: string }
  },
  actors: {
    appAuth: authMachine,
    appRouter: appRouterMachine,
    appSession: appSessionMachine,
  },
  guards: {
    isPublicRoute: ({ context }) => isPublicRoute(context.pathname),
    needsVerification: ({ context }) => {
      const s = context.sessionResult?.status
      return s === "unauthenticated" || s === "reauth_required"
    },
    noWalletOnProtectedRoute: ({ context }) =>
      context.address == null && !isPublicRoute(context.pathname),
    noWalletOnPublicRoute: ({ context }) =>
      context.address == null && isPublicRoute(context.pathname),
    isDisconnect: ({ context, event }) =>
      context.address != null &&
      event.type === "WALLET_STATE" &&
      event.address == null,
    isDisconnectOnPublicRoute: ({ context, event }) =>
      context.address != null &&
      event.type === "WALLET_STATE" &&
      event.address == null &&
      isPublicRoute(context.pathname),
    isAuthorizedOnLogin: (
      { context },
      params: { status: SessionResult["status"] }
    ) => params.status === "authorized" && context.pathname === "/login",
    needsVerificationOnLogin: (
      { context },
      params: { status: SessionResult["status"] }
    ) =>
      (params.status === "unauthenticated" ||
        params.status === "reauth_required") &&
      context.pathname === "/login",
    isConnect: ({ context, event }) =>
      !context.awaitingDisconnect &&
      context.address == null &&
      event.type === "WALLET_STATE" &&
      event.address != null,
    isAddressChange: ({ context, event }) =>
      context.address != null &&
      event.type === "WALLET_STATE" &&
      event.address != null &&
      (event.address !== context.address ||
        event.chainType !== context.chainType),
    notAwaitingDisconnect: ({ context }) => !context.awaitingDisconnect,
    isPathnameLogin: ({ event }) =>
      event.type === "PATHNAME_CHANGED" && event.pathname === "/login",
    isPathnameNotLogin: ({ event }) =>
      event.type === "PATHNAME_CHANGED" && event.pathname !== "/login",
    isPathnameContextLogin: ({ context }) => context.pathname === "/login",
    isPathnameProtected: ({ event }) =>
      event.type === "PATHNAME_CHANGED" && !isPublicRoute(event.pathname),
    isPathnameNotLoginAndNeedsVerification: ({ context, event }) => {
      const s = context.sessionResult?.status
      return (
        event.type === "PATHNAME_CHANGED" &&
        event.pathname !== "/login" &&
        (s === "unauthenticated" || s === "reauth_required")
      )
    },
    isAuthorizedSession: (_, params: { status: SessionResult["status"] }) =>
      params.status === "authorized",
    needsVerificationSession: (
      _,
      params: { status: SessionResult["status"] }
    ) =>
      params.status === "unauthenticated" ||
      params.status === "reauth_required",
    isBannedSession: (_, params: { status: SessionResult["status"] }) =>
      params.status === "banned",
    isRetryableSessionFailure: (
      _,
      params: { status: SessionResult["status"] }
    ) => params.status === "retryable_failure",
    hasWalletIdentity: ({ context }) =>
      context.address != null && context.chainType != null,
  },
  actions: {
    setWalletInfoFromEvent: assign(({ context, event }) => {
      if (event.type !== "WALLET_STATE") return {}
      const walletChanged =
        event.address !== context.address ||
        event.chainType !== context.chainType
      return {
        address: event.address,
        chainType: event.chainType,
        ...(walletChanged ? { sessionResult: null } : {}),
      }
    }),
    setPathnameFromChange: assign(({ event }) => {
      if (event.type !== "PATHNAME_CHANGED") return {}
      return {
        pathname: event.pathname,
        searchRedirect: event.searchRedirect ?? null,
      }
    }),
    captureRouteIntent: assign({
      routeIntent: ({ context }) =>
        isPublicRoute(context.pathname) ? null : context.pathname,
    }),
    clearRouteIntent: assign({ routeIntent: () => null }),
    setSessionResultFromEvent: assign(
      (_, params: { result: SessionResult }) => ({
        sessionResult: params.result,
      })
    ),
    clearWalletState: assign({
      address: () => null,
      chainType: () => null,
      sessionResult: () => null,
    }),
    markAwaitingDisconnect: assign({ awaitingDisconnect: () => true }),
    clearAwaitingDisconnect: assign({ awaitingDisconnect: () => false }),
    syncRouterContext: sendTo("appRouterActor", ({ context }) => ({
      type: "SYNC_CONTEXT",
      pathname: context.pathname,
      searchRedirect: context.searchRedirect,
      routeIntent: context.routeIntent,
    })),
    forwardToAuthActor: sendTo("authActor", ({ event }) => event),
    navigateToLogin: sendTo("appRouterActor", {
      type: "NAVIGATE_TO_LOGIN",
    }),
    navigateFromLoginIfNeeded: sendTo("appRouterActor", {
      type: "NAVIGATE_FROM_LOGIN_IF_NEEDED",
    }),
    navigateToBanned: sendTo("appRouterActor", { type: "NAVIGATE_TO_BANNED" }),
    sendStartVerification: sendTo(
      "authActor",
      (_, params: { address: string; chainType: string }) => ({
        type: "START_VERIFICATION" as const,
        address: params.address,
        chainType: params.chainType,
      })
    ),
    refreshRouter: () => {},
    clearSession: () => {},
    completeAuthJourney: () => {},
    clearAuthJourneyLease: () => {},
    logSessionCheckFailed: ({ context }, params: { error: unknown }) => {
      logger.warn("[appBootstrapMachine] session check failed", {
        error: params.error,
        address: context.address,
        chainType: context.chainType,
      })
    },
  },
  delays: {
    LEAVING_LOGIN_TIMEOUT: 3_000,
    HOME_STABILIZE_DELAY: 250,
    // Keep below AlertDialog enter (300ms) so verify mounts after /home paints.
    VERIFY_UI_SETTLE_DELAY: 120,
    VERIFY_LOGIN_ESCAPE_TIMEOUT: 3_000,
    NAVIGATE_TO_LOGIN_RETRY: 1_000,
    // JWT re-check only — does not require fresh wallet attestation.
    SESSION_EXPIRY: ({ context }) => {
      if (context.sessionResult?.status !== "authorized") return 60_000
      const msUntilExpiry = context.sessionResult.expiresAt - Date.now()
      return Math.max(msUntilExpiry, 30_000)
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QEMAOqC0AjA9jgLrPgE5oDEACgIIAqAEgHJUCyAogPoDCdVDA4qwAiAbQAMAXUShUOWAEt8cnADspIAB6IMARgAcAVm0A6AEwBOAGzb9AFgu6zoiwHYANCACeW8yZtH9zs76+ia+Qfq6AL6R7miYuAREpKhkVACq9OwAkgwAalQAMlmCtEJikkggMvKKKmqaCBgmgcZmZrqinRY2JgDMorruXo0mwc6mzfpmvVa6utq90bHo2HiEJOQAQrwMQuyCrDSsnEciEmrVCkqqlQ069iZGvSEzon3aos72Q976vb1GIImbR2Az6UQ2MxLEBxVaJDYpADqhQKh3YAGUaKVyhdZFc6rdvBDRID-gteuYbA5Aj8Rn8zE9DM4PnYLM0TNDYQl1skyMiCqiaBisUdhNoKtI8bUbqA7iDmaYzIEbFS2mY9BZaU1+uN+r15m0pr1tJyVtykmgjOaEag5MooEZiHB8DhiHaoOwAO7IAA2PrA+FSGTo2TyhWKpTOEqqUuu9S0vR64xMFnB2iC2gsWe6WuBViMc2mb3s0zMVNN8TWFtQVqrNvdjudrvdXt9-sD-MFwux50ql2l8caiZ6Bezzl8dkzNlzzXGjkh-zezgcHJiMLNdeStfhyVt9sbRGb9tbfoDZHRWT4DHYAHkMji+7GCbKE6D-M5E2XRI5RpmtdolRJFV+l0ZkOjBCs4R5S1rV3BsnUPN1j29U8OxRNEACVWExG8sKjXEajjQkh2aCwjG6fVpm0YEHH-QCjGAgYwIGYJINgmDNzieC4BwH0ADcW1gOB5BUc9L2vO8aAfSVCOfDQtA+f4njBKwImNfV-0MR4p3aDozBMeZPjYzia3Y9BuNgXiBOPITYBE5Q+XQoVMR7aN+yIl8h1A4xgicXp2n055NU8BSQR8mwKWCVTQIiYydw4+LzP3BCrME4TriMKBkEUe0yGkmNZJleShysPx5mNT5-KsCxek02wjDLACZg+dpmiiNcuRM7doKSh0Uv4tK7IyrKcqgPLxQI-EiruGYTBJTMDBq7oQlsf99TI-UAJ6Ww1XTOKeu66s9z6niBps9KVEy7L3Tykw3KfaaEz0BldE-PRdBVD7guGHR1uUuamI+D7Fg6jdEsO+tktO6yPVs+yjAAYwACzABGAGsbogFQwCMO0+JwNGcc68GzOOg9UvOobLuR1GMftBA8ZwBHrpUcp8vcuSZv6YxtEzYFIpscc3BCxoWXmrMfF0UIU0CfbqwhuCocss7YYu5REZR9HMex3HlHxwmjGJg7SYsinVap9Waa1+nGeZ6U2Ymx9CsHDBnl8IwFoWZdJm0LV1UeV6LEcIOBiXVdlkrEmTLJ-qYfYOGMqtumxqx5QccZg2jflk2lbN+O1Y12n3QZvWmZZ5Q2buyaB2I13HBJUPwUcCkDKcLUswZAJdHsCkAPTIzQcj43o9NlX84twvrZTnWM6JsHh8SmPocG+Gk+L23y7Z3p7ud2uemBJ5AnMGrBchb6tHBcWbBCdNLHVKlnDlhEFa43Ox4T6nNeTshU-T0vM-ntnEeb844f0tl-depc7bXDZjYHeU0XbX2eB7Z4ERRCZn9r7EWrtr4e18D3Nk1gAggwjlBIBi9R6gILmvXKv9db6znkPchPUl7KyoRPGhUAS742gazCQwh9DwJrp5DAgtUwMQ-ILOa6Y9C1WwXocYX1pj6iCguGwT8tw5xOmwleicIG5TAMQYgrojCoB9NlAAZq6AAtobQBz8tHk3ftQ-RXCN7234b2GSCDa7GnaEYb8EtmjtE6NMf8RCJHWH8q9ACQQNEJRYQ2MA6grjHh9DgKAdoxJXlvPeLxBUfEiL6CmBqkIPgPA+kxWkERHjAlsBRFUUVw7riYQ44BDpkmpI9OkzJ6tlDIAEiNG61B6BMDYFwHg-Ayj5I5o9IcEUebAj7gsdowR27jGcHYXoQIymZn0PE0y7SjCdNGuwHpdojD9MGddXKIzGAsA4NwXgAgRCO28cI4qrsVHkTAgBCWCx9C0nzPoHudhISiHpO0A5L9erHJSac85fSBlyCGbldQRBspEwsfgQxAAKVEVBcg5D4OwAoN4+A5HYDQLIbBJIAEoyBZzaRQ-cJyWyIsuci1FUB2YPRdvqSEAT0E1Uql8UCwthj6EBCCWw44JwrOaNCxxbK0kZIuVclFNyxrovwJiw22K8UEqJfwUl5LKXUtpRkBlTLNFHJVd0tVSLrmjTFEIjynz9TNEBMySYE4UwpiBeLfSEVRDbMpDYE0g8yHMsSay+F7LHVGAxVgOQPo5AAC8bo6r1cgA1xBcV0BvOMlymwshFAAFocAOAUKgABNa19jbUso6fG1VvSk26pTWmzN9peW7xETMQWASQTFgcOgyw04Rb9D8ExMwILbAQvHM8aF8goBpwgOwLJF4cmST7YUz5c1ugeyzCqHoVIFyApFjU0wRDLDZlGPMFdKL12bocp2NELlRQzL5b4noJIyzODnbzEIgtBhXvsKSBwhDeYn32VGsySbn2QFfZQWg9zxlPKmfhJ2+67ikUeJYbubwbCh3PggEFuhIMOCmOCZciYn1ruQ1k7NOL9U4vzeibCF4bzXlYAADQoFkDC9bGWNstKul9do90fJmuggEAQZb9G6IaQNZEg6HvmMEKYDhoV8UMXICxHgbrvuciKVg0n3UzX0jzfyzxu5sm-Je4Y0sGSbLaM0awvNmS6f04Z4ZaGxmPMmS8iznMExRQCfpBTDgPq0RFtRCI5ElTWHQV8UY2yfNuj87lXIrAMJZAAGJZE4LQLIPGuBFooIKcz37+0eszACf4hgQV92ZImWkoQsymDeIR4E+lAPNJtZaPTWWjO5W3RJPJbqwsjAMAyPQzQqR9BcA4SdznfAAk6zMFuFII0WEywZsbY1QtzKaGWAEEI+h7f+ILMwtJ5RSrLFYBYJGDAATg6QhDI3DsNiEvgfAabjwAFc5CodGQ8iZzzpnTdO0FMifxAivQjXMWwa3EDWClg1aYoE+hTF8Iq+DXVvvZYdH9gHLYQdkBY1i9juLcv5YK7W9gaQsgYkODQVE+xWA1pE0NmsxOjtJoDOT4HcgTv8r+H4cEx9onrRMPdudupr4ghi58QDUJCfgwF794XgOPSU+p2xvF9PCtM7JRS3j6ISsUA4Ba1g9LROtK3Nr-cZO9fsBB+L3xqkGIBT1CG1MEr0fjk7hEEJ3QymP01wdMBHvlBOmQMjZAWB-RkCwjQYTbP0TcYYF7opfxamfH9c4CE-wyxanmMYEvFh0HF6zOOFdas48J6TynsA2TJtSVq7h7wcrGRSw6D10iFfeaAghXMUNELLAfZadGrcEnkM4CB-gUxQOU9yARuwYxy+wCbogKnkz3Yv0w5dk1ebbI5hHz238LUSCfmnyg4aMsDGX1L5X6gNfabN-b5xXv1PdzAtIcsM89PkAJ-FRhmhjRA8-E0dGg2gfI5svg2hT0PwX9F9l9OVnUWwXQzlHUgxMgch8gigShTgQC5QVQfJbBExUwVo25sFAgGR1RUslQeh-g4lo95YF8N039MDNVTkcDEUwd0MgsodsN3lLMFImR-BphbB3sWQQU-YSk3g-gOhAggh9I0DuCMCNVuV2ABC8DD9P0asT9a55RjBvwyQSMz05gyNREDJyJHMXhgQaJ1EODn4uD2AeCsBkBlAX0U8mZCYIAO9cku8TCRFnpFFSpiEQRkt25MdXopgetMxmQAhog1xlAcAIA4A1A+dq4JDGgqQFgJFExxxUtZFcxmQNlJhphZhyolV2k8iZs64qQAkOgm5Q1QgOhbDlspUrDkCugIh2DPsupHEEIXQkIPQUJ2xGjYcSNO4lM5N+tgRcxPg-BAgAgnAnAANI1hio5m0nF2F7IZj+V7BkwnBlQKRlxkj-wS9Hgb4phNlbBQh6M3Cm1Y1tE84wErpRpjjvd-JusXBNlLj5gXB-wfc3gokdRu5DBXDdiF53iDjdFP4i57RfiB1nDTAOgNQIQrA3g1pRhyJDJphIQIgVR9tXiEkjoklW0HVek0SPVqQhUGtRVTig8RgRwGCAJUsg4rAo84TmEqS40ulcD20dCtV6SZo8cAk-hUslx69-wBhTBAN0waoQIIRZYKTDl9j7URSLlk1U0M13QJTwt1QlT0xH8lEOsAIPYkFHBFdngZVNDX1jSSISkSjZwIUXA5onNEAI1jBTipEZEUwa8Z8+cjAXcoAXTXYswARIQHB-clMAh7tPpfd-JkjFs0wDsSchd-t3cQcozhx-SoDZDbt2h7scd-Aswdo+h9Ru4+TZ8vtfNBdE9FA9MCzwQyprBgz65mQ50OtiQngwh0EvZ6QSEGyupY8gd48wBE8kZk9-QoyUxMdANIQyw502QXBbC7SGpJFxx5gvgeSnS39FycTTAQgPw9kLzy95F0wARASQV-hUwVkjyMCP919v839d85B98wAoznpeiyxQI3YPxghYCdABgyJ0xwgHTXt2p+T3CkMtCV8xT+CcBdSipZlT87ASQaDDBnhtkKD1kpUa8IQI1iR5QNd4L59ELPCMDvDfDkN-D0ZIA-z9IyIIploPgaMlR5dsEEibT5gfAaoYo4NoggA */
  id: "app-bootstrap",
  initial: "bootstrapping",
  context: ({ input }) => ({
    pathname: input.pathname,
    searchRedirect: null,
    address: null,
    chainType: null,
    sessionResult: null,
    routeIntent: null,
    awaitingDisconnect: false,
  }),

  invoke: [
    {
      id: "authActor",
      src: "appAuth",
    },
    {
      id: "appRouterActor",
      src: "appRouter",
      input: ({ context }) => ({
        pathname: context.pathname,
        searchRedirect: context.searchRedirect,
        routeIntent: context.routeIntent,
      }),
    },
  ],

  on: {
    PATHNAME_CHANGED: {
      actions: ["setPathnameFromChange", "syncRouterContext"],
    },
    AUTH_INVALIDATED: {
      target: ".bootstrapping.resolving_session",
    },
    // Must be global: ban can fire during restoring_wallet before WALLET_RESTORED.
    BANNED_DETECTED: {
      target: ".signed_out.banned_blocked",
      actions: "clearAuthJourneyLease",
    },
    // clearSession clears active cookies only; per-wallet refresh survives (#223).
    // After SIGN_OUT, isConnect stays blocked until disconnect (awaitingDisconnect)
    // so a still-connected wallet cannot silent-reauth. Address changes remain
    // allowed on public_route_idle (user picked another wallet).
    WALLET_STATE: [
      {
        guard: "isDisconnectOnPublicRoute",
        target: ".signed_out.public_route_idle",
        actions: [
          "clearSession",
          "clearWalletState",
          "clearAwaitingDisconnect",
          "clearAuthJourneyLease",
        ],
      },
      {
        guard: "isDisconnect",
        target: ".signed_out.navigating_to_login",
        actions: [
          "clearSession",
          "clearWalletState",
          "clearAwaitingDisconnect",
          "clearAuthJourneyLease",
        ],
      },
      {
        // SIGN_OUT already cleared address, or disconnect reported twice.
        guard: ({ context, event }) =>
          context.awaitingDisconnect &&
          event.type === "WALLET_STATE" &&
          event.address == null,
        actions: "clearAwaitingDisconnect",
      },
    ],
  },

  states: {
    bootstrapping: {
      initial: "restoring_wallet",
      states: {
        restoring_wallet: {
          tags: ["initializing"],
          on: {
            AUTH_INVALIDATED: {},
            // Disconnects bubble to root; forward everything else to auth.
            WALLET_STATE: {
              guard: not(or(["isDisconnect", "isDisconnectOnPublicRoute"])),
              actions: "forwardToAuthActor",
            },
            SIGN_OUT: {
              target: "#app-bootstrap.signed_out.navigating_to_login",
              actions: [
                "clearRouteIntent",
                "markAwaitingDisconnect",
                "clearAuthJourneyLease",
              ],
            },
            WALLET_RESTORED: {
              target: "resolving_session",
              actions: assign(({ event }) => ({
                address: event.address,
                chainType: event.chainType,
              })),
            },
          },
        },

        resolving_session: {
          tags: ["initializing"],
          initial: "gating",
          on: {
            SIGN_OUT: {
              target: "#app-bootstrap.signed_out.navigating_to_login",
              actions: [
                "clearRouteIntent",
                "markAwaitingDisconnect",
                "clearAuthJourneyLease",
              ],
            },
            WALLET_STATE: [
              {
                guard: "isAddressChange",
                target: "#app-bootstrap.bootstrapping.restoring_wallet",
                actions: [
                  "clearAuthJourneyLease",
                  "setWalletInfoFromEvent",
                  "forwardToAuthActor",
                ],
              },
              {
                guard: "isConnect",
                target: "#app-bootstrap.bootstrapping.restoring_wallet",
                actions: ["setWalletInfoFromEvent", "forwardToAuthActor"],
              },
            ],
          },
          states: {
            gating: {
              always: [
                {
                  guard: "noWalletOnProtectedRoute",
                  target: "#app-bootstrap.signed_out.navigating_to_login",
                  actions: [
                    "clearAuthJourneyLease",
                    "captureRouteIntent",
                    "syncRouterContext",
                  ],
                },
                {
                  guard: "noWalletOnPublicRoute",
                  target: "#app-bootstrap.signed_out.public_route_idle",
                  actions: "clearAuthJourneyLease",
                },
                { target: "checking" },
              ],
            },
            checking: {
              invoke: {
                src: "appSession",
                input: ({ context }) => {
                  if (context.address == null || context.chainType == null) {
                    throw new Error(
                      "resolving_session.checking entered without wallet identity"
                    )
                  }
                  return {
                    address: context.address,
                    chainType: context.chainType,
                  }
                },
                onDone: [
                  {
                    guard: {
                      type: "isRetryableSessionFailure",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.session_unreachable",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                  {
                    guard: {
                      type: "isBannedSession",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.signed_out.banned_blocked",
                    actions: [
                      "clearAuthJourneyLease",
                      {
                        type: "setSessionResultFromEvent",
                        params: ({ event }) => ({ result: event.output }),
                      },
                    ],
                  },
                  {
                    guard: {
                      type: "isAuthorizedOnLogin",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.bootstrapping.exiting_login",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                  {
                    guard: {
                      type: "needsVerificationOnLogin",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.verifying.settling_ui",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                  {
                    guard: {
                      type: "isAuthorizedSession",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.signed_in",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                  {
                    guard: {
                      type: "needsVerificationSession",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.verifying.settling",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                ],
                onError: {
                  target: "#app-bootstrap.session_unreachable",
                  actions: [
                    {
                      type: "logSessionCheckFailed",
                      params: ({ event }) => ({ error: event.error }),
                    },
                    assign({
                      sessionResult: () => ({
                        status: "retryable_failure",
                        reason: "Session check failed",
                      }),
                    }),
                  ],
                },
              },
            },
          },
        },

        exiting_login: {
          tags: ["initializing"],
          entry: "navigateFromLoginIfNeeded",
          initial: "navigating",
          on: {
            SIGN_OUT: {
              target: "#app-bootstrap.signed_out.navigating_to_login",
              actions: [
                "clearRouteIntent",
                "markAwaitingDisconnect",
                "clearAuthJourneyLease",
              ],
            },
          },
          states: {
            navigating: {
              after: {
                LEAVING_LOGIN_TIMEOUT: [
                  {
                    guard: and([
                      "needsVerification",
                      not("isPathnameContextLogin"),
                    ]),
                    target: "stabilizing",
                  },
                  {
                    // Still on /login — keep splash and retry nav (avoid login flash).
                    guard: "isPathnameContextLogin",
                    target: "navigating",
                    reenter: true,
                    actions: ["navigateFromLoginIfNeeded"],
                  },
                  { target: "#app-bootstrap.signed_in" },
                ],
              },
              on: {
                PATHNAME_CHANGED: [
                  {
                    guard: "isPathnameNotLoginAndNeedsVerification",
                    target: "stabilizing",
                    actions: ["setPathnameFromChange", "syncRouterContext"],
                  },
                  {
                    guard: "isPathnameNotLogin",
                    target: "#app-bootstrap.signed_in",
                    actions: ["setPathnameFromChange", "syncRouterContext"],
                  },
                ],
              },
            },
            stabilizing: {
              after: {
                HOME_STABILIZE_DELAY: {
                  target: "#app-bootstrap.verifying.settling_ui",
                },
              },
            },
          },
        },
      },
    },

    signed_in: {
      tags: ["signed_in"],
      entry: [
        "completeAuthJourney",
        "refreshRouter",
        "navigateFromLoginIfNeeded",
      ],
      initial: "idle",
      on: {
        SIGN_OUT: {
          target: "signed_out.navigating_to_login",
          actions: [
            "clearRouteIntent",
            "markAwaitingDisconnect",
            "clearAuthJourneyLease",
          ],
        },
        // Relative target — reenter: true would re-fire refreshRouter.
        AUTH_INVALIDATED: { target: ".revalidating" },
        WALLET_STATE: {
          guard: "isAddressChange",
          target: ".revalidating.gating",
          actions: [
            "clearAuthJourneyLease",
            "setWalletInfoFromEvent",
            "forwardToAuthActor",
          ],
        },
        PATHNAME_CHANGED: {
          guard: "isPathnameLogin",
          actions: [
            "setPathnameFromChange",
            "syncRouterContext",
            "navigateFromLoginIfNeeded",
          ],
        },
      },
      states: {
        idle: {
          after: {
            SESSION_EXPIRY: { target: "revalidating" },
          },
        },
        revalidating: {
          initial: "checking",
          states: {
            gating: {
              on: {
                AUTH_INVALIDATED: {},
                WALLET_STATE: [
                  {
                    guard: "isAddressChange",
                    actions: ["clearAuthJourneyLease", "forwardToAuthActor"],
                  },
                  {
                    guard: not(
                      or(["isDisconnect", "isDisconnectOnPublicRoute"])
                    ),
                    actions: "forwardToAuthActor",
                  },
                ],
                WALLET_RESTORED: {
                  target: "checking",
                  actions: assign(({ event }) => ({
                    address: event.address,
                    chainType: event.chainType,
                  })),
                },
              },
            },
            checking: {
              invoke: {
                src: "appSession",
                input: ({ context }) => {
                  if (context.address == null || context.chainType == null) {
                    throw new Error(
                      "signed_in.revalidating.checking entered without wallet identity"
                    )
                  }
                  return {
                    address: context.address,
                    chainType: context.chainType,
                  }
                },
                onDone: [
                  {
                    guard: {
                      type: "isBannedSession",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.signed_out.banned_blocked",
                    actions: [
                      "clearAuthJourneyLease",
                      {
                        type: "setSessionResultFromEvent",
                        params: ({ event }) => ({ result: event.output }),
                      },
                    ],
                  },
                  {
                    guard: {
                      type: "needsVerificationSession",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.verifying.settling",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                  {
                    // Fail open: keep prior sessionResult.
                    guard: {
                      type: "isRetryableSessionFailure",
                      params: ({ event }) => ({ status: event.output.status }),
                    },
                    target: "#app-bootstrap.signed_in.idle",
                  },
                  {
                    target: "#app-bootstrap.signed_in.idle",
                    actions: {
                      type: "setSessionResultFromEvent",
                      params: ({ event }) => ({ result: event.output }),
                    },
                  },
                ],
                onError: {
                  target: "#app-bootstrap.signed_in.idle",
                  actions: {
                    type: "logSessionCheckFailed",
                    params: ({ event }) => ({ error: event.error }),
                  },
                },
              },
            },
          },
        },
      },
    },

    verifying: {
      tags: ["verifying"],
      entry: "navigateFromLoginIfNeeded",
      initial: "settling_ui",
      always: {
        guard: not("hasWalletIdentity"),
        target: "signed_out.navigating_to_login",
        actions: "clearAuthJourneyLease",
      },
      on: {
        AUTH_INVALIDATED: {},
        WALLET_STATE: {
          guard: "isAddressChange",
          target: "bootstrapping.restoring_wallet",
          actions: [
            "clearAuthJourneyLease",
            "setWalletInfoFromEvent",
            "forwardToAuthActor",
          ],
        },
        PATHNAME_CHANGED: {
          guard: "isPathnameLogin",
          actions: [
            "setPathnameFromChange",
            "syncRouterContext",
            "navigateFromLoginIfNeeded",
          ],
        },
        VERIFICATION_COMPLETE: {
          target: "bootstrapping.resolving_session",
        },
        SIGN_OUT: {
          target: "signed_out.navigating_to_login",
          actions: [
            "clearRouteIntent",
            "markAwaitingDisconnect",
            "clearAuthJourneyLease",
          ],
        },
      },
      states: {
        settling_ui: {
          tags: ["initializing"],
          after: {
            VERIFY_UI_SETTLE_DELAY: {
              target: "active",
              guard: and(["hasWalletIdentity", not("isPathnameContextLogin")]),
            },
            VERIFY_LOGIN_ESCAPE_TIMEOUT: {
              guard: "isPathnameContextLogin",
              target: "settling_ui",
              reenter: true,
              actions: ["navigateFromLoginIfNeeded"],
            },
          },
          on: {
            PATHNAME_CHANGED: {
              guard: "isPathnameNotLogin",
              target: "settling_ui",
              reenter: true,
              actions: ["setPathnameFromChange", "syncRouterContext"],
            },
          },
        },
        // Off-login verify arming; not tagged initializing.
        settling: {
          after: {
            VERIFY_UI_SETTLE_DELAY: {
              target: "active",
              guard: "hasWalletIdentity",
            },
          },
        },
        active: {
          entry: {
            type: "sendStartVerification",
            params: ({ context }): { address: string; chainType: string } => {
              const { address, chainType } = context
              if (address == null || chainType == null) {
                throw new Error(
                  "verifying.active entered without wallet identity"
                )
              }
              return { address, chainType }
            },
          },
        },
      },
    },

    session_unreachable: {
      tags: ["session_unreachable"],
      on: {
        AUTH_INVALIDATED: {},
        RETRY_SESSION: {
          target: "bootstrapping.resolving_session.checking",
          actions: assign({ sessionResult: () => null }),
        },
        SIGN_OUT: {
          target: "signed_out.navigating_to_login",
          actions: [
            "clearRouteIntent",
            "markAwaitingDisconnect",
            "clearAuthJourneyLease",
          ],
        },
      },
    },

    signed_out: {
      tags: ["signed_out"],
      initial: "public_route_idle",
      states: {
        public_route_idle: {
          on: {
            // SIGN_OUT keeps address until disconnect — treat switch as connect.
            // isConnect blocked while awaitingDisconnect (cancel/silent-reauth race).
            WALLET_STATE: [
              {
                guard: "isAddressChange",
                target: "#app-bootstrap.bootstrapping.restoring_wallet",
                actions: [
                  "clearAwaitingDisconnect",
                  "setWalletInfoFromEvent",
                  "forwardToAuthActor",
                ],
              },
              {
                guard: "isConnect",
                target: "#app-bootstrap.bootstrapping.restoring_wallet",
                actions: ["setWalletInfoFromEvent", "forwardToAuthActor"],
              },
            ],
            PATHNAME_CHANGED: {
              guard: "isPathnameProtected",
              target: "navigating_to_login",
              actions: assign({
                pathname: ({ event }) => event.pathname,
                searchRedirect: ({ event }) => event.searchRedirect ?? null,
                routeIntent: ({ event }) => event.pathname,
              }),
            },
          },
        },

        navigating_to_login: {
          tags: ["initializing"],
          entry: ["syncRouterContext", "navigateToLogin"],
          // Already on /login → no PATHNAME_CHANGED; don't wait forever.
          always: {
            guard: "isPathnameContextLogin",
            target: "public_route_idle",
          },
          after: {
            // Soft-nav can drop the first replace; retry until /login commits.
            NAVIGATE_TO_LOGIN_RETRY: {
              guard: not("isPathnameContextLogin"),
              target: "navigating_to_login",
              reenter: true,
            },
          },
          on: {
            AUTH_INVALIDATED: {},
            PATHNAME_CHANGED: {
              guard: "isPathnameLogin",
              target: "public_route_idle",
              actions: ["setPathnameFromChange", "syncRouterContext"],
            },
            WALLET_STATE: [
              {
                // Defer connects until public_route_idle — isConnect here after
                // disconnect clears the latch can bounce into restoring_wallet
                // and leave the splash stuck on /home if /login never commits.
                guard: and(["isAddressChange", "notAwaitingDisconnect"]),
                target: "#app-bootstrap.bootstrapping.restoring_wallet",
                actions: ["setWalletInfoFromEvent", "forwardToAuthActor"],
              },
              {
                guard: "isDisconnect",
                actions: [
                  "clearSession",
                  "clearWalletState",
                  "clearAwaitingDisconnect",
                  "clearAuthJourneyLease",
                ],
              },
            ],
          },
        },

        banned_blocked: {
          entry: "navigateToBanned",
          on: {
            SIGN_OUT: {
              target: "navigating_to_login",
              actions: [
                "clearRouteIntent",
                "markAwaitingDisconnect",
                "clearAuthJourneyLease",
              ],
            },
          },
        },
      },
    },
  },
})
